"use client";

import { useEffect, useState } from "react";
import Script from "next/script";

import { isTrackingAllowed, onConsentChange } from "@/lib/consent";
import { SITE_CONFIG } from "@/config/site";

const { GA_MEASUREMENT_ID, LINKEDIN_PARTNER_ID } = SITE_CONFIG;

// `lintrk` is already declared in app/lib/linkedin.ts — redeclaring it here
// with a different shape is a TS conflict, and this file only touches it from
// inside the inline script string anyway.
declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

/**
 * Makes "Reject all" in the cookie banner actually stop tracking, and loads the
 * LinkedIn Insight tag (which never loaded at all — see below).
 *
 * TWO BUGS THIS FIXES
 *
 * 1. Rejecting consent did nothing. The banner wrote "rejected" to
 *    sessionStorage and hid itself; GA, Clarity, LinkedIn and the Meta Pixel
 *    all carried on regardless. A banner that promises a choice it doesn't
 *    honour is worse than no banner.
 *
 * 2. LinkedIn never tracked anything. Its snippet was wrapped in
 *    window.addEventListener('load', …) inside a next/script with
 *    strategy="lazyOnload". lazyOnload runs *after* the window load event has
 *    already fired, so that listener could never run — window.lintrk was never
 *    defined, and every linkedInEvent() call silently no-opped. It is loaded
 *    directly here instead.
 *
 * Opt-out is per-vendor because there is no common mechanism:
 *   GA4    — window['ga-disable-<ID>'] = true (official; works before or after load)
 *   Meta   — fbq('consent', 'revoke')
 *   Clarity— clarity('consent', false)
 *   LinkedIn — no opt-out API, so we simply never load it when rejected
 */
export default function ConsentAwareAnalytics() {
  // Consent can only be read in the browser, so the first render — server and
  // client alike — must render nothing. Deciding during render instead would
  // make the server output (no script) disagree with the client's (script) and
  // trip a hydration mismatch.
  const [linkedInAllowed, setLinkedInAllowed] = useState(false);

  useEffect(() => {
    const applyOptOut = () => {
      if (isTrackingAllowed()) return;

      // GA4: set before gtag.js executes and it never sends a hit. This effect
      // runs at hydration; the GA scripts are lazyOnload, so we are ahead of them.
      window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;

      try {
        window.fbq?.("consent", "revoke");
      } catch {
        /* pixel not loaded yet — the loader checks consent itself */
      }

      try {
        window.clarity?.("consent", false);
      } catch {
        /* clarity not loaded yet */
      }
    };

    applyOptOut();
    setLinkedInAllowed(isTrackingAllowed());

    return onConsentChange(() => {
      applyOptOut();
      setLinkedInAllowed(isTrackingAllowed());
    });
  }, []);

  // Never loaded for a visitor who has rejected. Unlike GA and Meta, the
  // LinkedIn tag has no documented way to switch off once it is running, so the
  // only reliable control is not loading it in the first place.
  if (!linkedInAllowed) return null;

  return (
    <Script id="linkedin-insight" strategy="lazyOnload">
      {`
        window._linkedin_partner_id = "${LINKEDIN_PARTNER_ID}";
        window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
        window._linkedin_data_partner_ids.push(window._linkedin_partner_id);

        if (!window.lintrk) {
          window.lintrk = function(a, b) { window.lintrk.q.push([a, b]) };
          window.lintrk.q = [];
        }

        var s = document.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
        document.head.appendChild(s);
      `}
    </Script>
  );
}
